import { createRoot } from "react-dom/client";
import { AppStoresFactory } from "../shared-components/AppStoresFactory";
import { UploadMasterComponent } from "./UploadMasterComponent";
import { AppUploadFactory } from "./AppUploadFactory";
import { configureLogging } from "../shared-components/configuration/ConfigureLogging";
import { environment } from "../util/env/environment";
import { Fetcher } from "../util/io/Fetcher";
import type { UploadIndexViewModelModel } from "../../OpenApi/api";
import React from "react";
import { OopsPageErrorCatchingComponent } from "../shared-components/error-catching-component/OopsPageErrorCatchingComponent";
import { SmoothLoadingComponent } from "../util/components/SmoothLoadingComponent";
import { InitialResourcesLoader, type IInitialResourcesLoader } from "../resources/InitialResourcesLoader";
import { redirectIfEolBrowser } from "../shared-components/configuration/EolBrowserGuard";

(async () => {
    if (redirectIfEolBrowser(window)) {
        return;
    }
    const rootElement: HTMLElement = document.getElementById("app-root")!;
    const root = createRoot(rootElement);

    // Show a loading indicator immediately so the user doesn't stare at a blank page while bootstrapping.
    root.render(<SmoothLoadingComponent />);

    try {
        const fetcher = new Fetcher();
        const appUploadFactory = new AppUploadFactory(fetcher, new AppStoresFactory(fetcher, window));
        const viewModelUrl = rootElement.dataset.viewModelUrl!;

        // Kick off the resources fetch in parallel with the view-model. data-resources-url is always emitted
        // by the server (see ResourcesController.GetResourcesUrl); a missing value is a bug.
        const resourcesUrl = rootElement.dataset.resourcesUrl;
        if (!resourcesUrl) {
            throw new Error("data-resources-url is missing on the app-root element");
        }
        const initialResourcesLoader: IInitialResourcesLoader = InitialResourcesLoader.start(fetcher, resourcesUrl);

        const viewModel: UploadIndexViewModelModel = await fetcher.getJSON<UploadIndexViewModelModel>(viewModelUrl);

        const { sourcesAvailableForDicomUpload, preferredSourceForDicomUpload, preferredSourceName, layout } = viewModel;
        if (sourcesAvailableForDicomUpload.length === 0) {
            console.warn("Tried to access the file upload page, but no sources allow DICOM file upload");
            window.location.href = `/POW3/Account/Login/${preferredSourceName ?? "Global"}`;
            return;
        }

        if (!preferredSourceForDicomUpload && preferredSourceName && preferredSourceName.trim() !== "" && preferredSourceName !== "Global") {
            console.warn(`Tried to access the file upload page of source ${preferredSourceName}, but the source does not allow DICOM file upload`);
            window.location.href = `/POW3/Account/Login/${preferredSourceName}`;
            return;
        }

        const enableFrontendLogging = layout.sessionInfo?.currentUser?.settings?.generalUserSettings.enableFrontendLogging ?? false;
        configureLogging(enableFrontendLogging, environment);

        const appUpload = await appUploadFactory.create(viewModel, initialResourcesLoader);

        root.render(<UploadMasterComponent appUpload={appUpload} />);
    } catch (error) {
        root.render(<OopsPageErrorCatchingComponent error={error} isLandingPage={true} />);
    }
})();
